import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Alert, Button, Container, Field, Input, Panel, Select, Textarea } from '../components/site/ui';
import { IconCheckCircle, IconClock, IconMail, IconShield } from '../components/icons';
import { fieldErrorsFrom } from '../utils/formErrors';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const HELP_POINTS = [
  {
    icon: IconMail,
    title: 'We reply by email',
    description: 'Use an address you check — every answer goes back there.'
  },
  {
    icon: IconClock,
    title: 'Usually within one working day',
    description: 'Billing and case questions are picked up first.'
  },
  {
    icon: IconShield,
    title: 'Include your case ID if you have one',
    description: 'It lets us pull up the full history straight away.'
  }
];

const TOPIC_OPTIONS = [
  { value: 'general', label: 'General enquiry' },
  { value: 'enterprise', label: 'Enterprise plan' },
  { value: 'courier', label: 'Courier provider' },
  { value: 'support', label: 'Support' },
  { value: 'billing', label: 'Billing' }
];

/**
 * Loads the reCAPTCHA v3 script once and resolves with `window.grecaptcha`.
 * Resolves to null with no site key configured — the server skips
 * verification too when its secret key is blank, so a dev machine with
 * neither still submits successfully end to end.
 */
let recaptchaPromise = null;
const loadRecaptcha = () => {
  if (!RECAPTCHA_SITE_KEY) return Promise.resolve(null);
  if (recaptchaPromise) return recaptchaPromise;

  recaptchaPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve(window.grecaptcha);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => resolve(window.grecaptcha);
    script.onerror = () => reject(new Error('Could not load reCAPTCHA'));
    document.head.appendChild(script);
  });
  return recaptchaPromise;
};

const getRecaptchaToken = async () => {
  if (!RECAPTCHA_SITE_KEY) return undefined;
  try {
    const grecaptcha = await loadRecaptcha();
    if (!grecaptcha) return undefined;
    return await new Promise((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action: 'contact' })
          .then(resolve)
          .catch(() => resolve(undefined));
      });
    });
  } catch {
    return undefined;
  }
};

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const topicParam = searchParams.get('topic');
  const initialTopic = TOPIC_OPTIONS.some((t) => t.value === topicParam) ? topicParam : 'general';

  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    topic: initialTopic,
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  // Warm the script early so submitting doesn't wait on the network fetch.
  useEffect(() => {
    loadRecaptcha().catch(() => {});
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      const recaptchaToken = await getRecaptchaToken();
      await api.post('/api/contact', { ...form, recaptchaToken });
      setSent(true);
    } catch (err) {
      setErrors(fieldErrorsFrom(err, { fallback: 'Could not send your message. Please try again.' }));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Container width="sm" className="py-12 sm:py-20">
        <Panel className="text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success-50 text-success-600 ring-1 ring-inset ring-success-100">
            <IconCheckCircle size={32} strokeWidth={1.5} />
          </span>
          <h1 className="mt-6 font-display text-2xl font-800 tracking-tight text-ink-900">
            Message sent
          </h1>
          <p className="mt-2 text-[15px] text-ink-600">
            Thanks for reaching out — we&apos;ll get back to you by email shortly.
          </p>
        </Panel>
      </Container>
    );
  }

  return (
    <>
      <Container width="full" className="py-8 sm:py-10">
        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:gap-12">
          {/* What to expect — gives the page a left edge so the form isn't
              marooned in the middle of an empty screen. */}
          <div className="lg:pt-2">
            <p className="text-[11px] font-700 uppercase tracking-[0.14em] text-brand-600">Help</p>
            <h1 className="mt-3 font-display text-[30px] font-800 leading-[1.1] tracking-tight text-ink-900 sm:text-[38px]">
              How can we <span className="text-accent-500">help?</span>
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-600">
              Questions about a case, a plan, or anything else — send a message and we&apos;ll reply
              by email.
            </p>

            <ul className="mt-7 space-y-3.5">
              {HELP_POINTS.map(({ icon: Icon, title, description }) => (
                <li key={title} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">
                    <Icon size={17} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-700 text-ink-900">{title}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-ink-600">{description}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-7 rounded-xl bg-ink-100 px-4 py-3.5">
              <p className="text-sm leading-relaxed text-ink-700">
                Chasing a specific delivery?{' '}
                <Link to="/report" className="font-600 text-brand-700 underline hover:text-brand-800">
                  Report the issue
                </Link>{' '}
                instead — it reaches the retailer with the evidence attached.
              </p>
            </div>
          </div>

          <Panel className="p-5 sm:p-6">
            <h2 className="font-display text-lg font-700 tracking-tight text-ink-900">
              Send us a message
            </h2>
            <p className="mt-1 text-sm text-ink-600">
              Pick the topic that fits best so it reaches the right person.
            </p>

            {errors._general && (
              <Alert tone="danger" className="mt-4">
                {errors._general}
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="mt-5 space-y-4" noValidate>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Name" htmlFor="contact-name" required error={errors.name}>
                  <Input
                    id="contact-name"
                    value={form.name}
                    onChange={set('name')}
                    autoComplete="name"
                    required
                    autoFocus
                  />
                </Field>

                <Field label="Work email" htmlFor="contact-email" required error={errors.email}>
                  <Input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    autoComplete="email"
                    required
                  />
                </Field>

                <Field label="Company" htmlFor="contact-company" error={errors.company}>
                  <Input
                    id="contact-company"
                    value={form.company}
                    onChange={set('company')}
                    placeholder="Optional"
                    autoComplete="organization"
                  />
                </Field>

                <Field label="What's this about?" htmlFor="contact-topic" error={errors.topic}>
                  <Select id="contact-topic" value={form.topic} onChange={set('topic')}>
                    {TOPIC_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <Field label="Message" htmlFor="contact-message" required error={errors.message}>
                <Textarea
                  id="contact-message"
                  value={form.message}
                  onChange={set('message')}
                  rows={5}
                  maxLength={5000}
                  required
                  placeholder="How can we help?"
                />
              </Field>

              <div className="flex flex-col gap-3 border-t border-ink-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                {RECAPTCHA_SITE_KEY ? (
                  <p className="text-xs leading-relaxed text-ink-500 sm:max-w-sm">
                    Protected by reCAPTCHA — Google&apos;s{' '}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-ink-700"
                    >
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-ink-700"
                    >
                      Terms
                    </a>{' '}
                    apply.
                  </p>
                ) : (
                  <span />
                )}
                <Button type="submit" size="lg" loading={loading} className="shrink-0">
                  Send message
                </Button>
              </div>
            </form>
          </Panel>
        </div>
      </Container>
    </>
  );
};

export default ContactPage;
